import React from 'react';
import PropTypes from 'prop-types';
import styles from 'components/common/CustomConfirmModal/CustomConfirmModal.module.css';
import resultStyles from './BulkOperationResultModal.module.css';

// Renders the per-item outcome of a bulk operation. Matches the role-and-deletion-design
// §5.5 contract: items that fail a guard are reported back with a reason, never forced.
// `nameLookup(id)` lets callers translate ids to friendly names; falls back to the raw id.

const BulkOperationResultModal = ({
  show,
  title,
  result,
  itemLabel = 'item',
  nameLookup,
  onClose,
}) => {
  if (!show || !result) return null;

  const { succeededCount = 0, skippedCount = 0, results = [] } = result;
  const skipped = results.filter((r) => !r.success);
  const total = succeededCount + skippedCount;

  // No overlay onClick — dialogs must not close on an outside click (only the × / Close).
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{title}</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={resultStyles.summary}>
            <strong>{succeededCount}</strong> of <strong>{total}</strong>{' '}
            {total === 1 ? itemLabel : `${itemLabel}s`} processed successfully.
            {skippedCount > 0 && (
              <>
                {' '}
                <span className={resultStyles.skippedLabel}>
                  {skippedCount} skipped.
                </span>
              </>
            )}
          </p>

          {skipped.length > 0 && (
            <div className={resultStyles.skippedList}>
              <h4 className={resultStyles.skippedHeading}>Not processed:</h4>
              <ul>
                {skipped.map((item) => {
                  const label =
                    (nameLookup && nameLookup(item.id)) || item.id;
                  return (
                    <li key={item.id}>
                      <strong>{label}</strong> — {item.reason || 'No reason given.'}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnPrimary} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

BulkOperationResultModal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  result: PropTypes.shape({
    succeededCount: PropTypes.number,
    skippedCount: PropTypes.number,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        success: PropTypes.bool,
        reason: PropTypes.string,
      })
    ),
  }),
  itemLabel: PropTypes.string,
  nameLookup: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default BulkOperationResultModal;
